/**
 * CacheValueInspectorModal
 *
 * Read-only view of a single Redis cache key — its type, TTL, size and stored value.
 */

import { Modal, Stack, Group, Text, Badge, Code, Loader, Alert } from '../../../../components/ui';
import { IconAlertCircle, IconClock, IconRuler2, IconQuestionMark } from '@tabler/icons-react';
import type { CacheKeyValue } from '../types';

interface CacheValueInspectorModalProps {
  opened: boolean;
  onClose: () => void;
  cacheKey: string;
  data: CacheKeyValue | null;
  loading: boolean;
  error?: string | null;
}

const formatTtl = (seconds: number | null): string => {
  if (seconds == null || seconds < 0) return 'No expiry (persistent)';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const prettyValue = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

export function CacheValueInspectorModal({
  opened,
  onClose,
  cacheKey,
  data,
  loading,
  error,
}: CacheValueInspectorModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="Cache key" size="xl">
      <Stack gap="md">
        <Code>{cacheKey}</Code>

        {loading && (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        )}

        {!loading && error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load value">
            {error}
          </Alert>
        )}

        {!loading && !error && data && !data.exists && (
          <Alert color="blue" icon={<IconQuestionMark size={16} />} title="Key not found">
            This key no longer exists — it may have expired or been evicted since the list was scanned.
          </Alert>
        )}

        {!loading && !error && data?.exists && (
          <>
            <Group gap="lg">
              <Badge color="grape" variant="light">
                {data.type}
              </Badge>
              <Group gap={4}>
                <IconClock size={14} />
                <Text size="xs" c="dimmed">
                  {formatTtl(data.ttlSeconds)}
                </Text>
              </Group>
              <Group gap={4}>
                <IconRuler2 size={14} />
                <Text size="xs" c="dimmed">
                  {formatBytes(data.sizeBytes)}
                </Text>
              </Group>
            </Group>

            {data.truncated && (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Value truncated">
                This value is large and has been truncated for display.
              </Alert>
            )}

            <Code block>{prettyValue(data.value)}</Code>
          </>
        )}
      </Stack>
    </Modal>
  );
}
